/**
 * AI Studio home hub — tile definitions grouped by product area.
 * Visibility is RBAC-driven: each tile maps to a navigation screen READ permission.
 */

import type { ComponentType, SVGProps } from 'react';
import {
  BeakerIcon,
  ChatBubbleLeftRightIcon,
  CpuChipIcon,
  CubeTransparentIcon,
  LightBulbIcon,
  PlusCircleIcon,
  ServerStackIcon,
  SparklesIcon,
  Squares2X2Icon,
  UserCircleIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

export interface AIStudioHomeTileDef {
  id: string;
  screenCode: string;
  route: string;
  titleKey: string;
  descriptionKey: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

export interface AIStudioHomeTileGroupDef {
  id: string;
  titleKey: string;
  tiles: AIStudioHomeTileDef[];
}

/** Ordered groups; empty groups are omitted after permission filtering. */
export const AI_STUDIO_HOME_TILE_GROUPS: AIStudioHomeTileGroupDef[] = [
  {
    id: 'work',
    titleKey: 'aiStudio.home.groups.work',
    tiles: [
      {
        id: 'instances',
        screenCode: 'AI_STUDIO_INSTANCES',
        route: '/ai-studio/instances',
        titleKey: 'aiStudio.home.tiles.instances.title',
        descriptionKey: 'aiStudio.home.tiles.instances.description',
        icon: ServerStackIcon,
      },
      {
        id: 'provision',
        screenCode: 'AI_STUDIO_INSTANCES',
        route: '/ai-studio/instances/provision',
        titleKey: 'aiStudio.home.tiles.provision.title',
        descriptionKey: 'aiStudio.home.tiles.provision.description',
        icon: PlusCircleIcon,
      },
      {
        id: 'catalog',
        screenCode: 'AI_STUDIO_CATALOG',
        route: '/ai-studio/catalog',
        titleKey: 'aiStudio.home.tiles.catalog.title',
        descriptionKey: 'aiStudio.home.tiles.catalog.description',
        icon: Squares2X2Icon,
      },
    ],
  },
  {
    id: 'settings',
    titleKey: 'aiStudio.home.groups.settings',
    tiles: [
      {
        id: 'personal-models',
        screenCode: 'AI_FEATURES',
        route: '/ai/settings',
        titleKey: 'aiStudio.home.tiles.personalModels.title',
        descriptionKey: 'aiStudio.home.tiles.personalModels.description',
        icon: UserCircleIcon,
      },
      {
        id: 'tenant-models',
        screenCode: 'TENANT_AI_SETTINGS',
        route: '/tenant/ai/settings',
        titleKey: 'aiStudio.home.tiles.tenantModels.title',
        descriptionKey: 'aiStudio.home.tiles.tenantModels.description',
        icon: CpuChipIcon,
      },
      {
        id: 'platform-models',
        screenCode: 'ADMIN_AI_SETTINGS',
        route: '/admin/ai/settings',
        titleKey: 'aiStudio.home.tiles.platformModels.title',
        descriptionKey: 'aiStudio.home.tiles.platformModels.description',
        icon: WrenchScrewdriverIcon,
      },
    ],
  },
  {
    id: 'factory',
    titleKey: 'aiStudio.home.groups.factory',
    tiles: [
      {
        id: 'agents',
        screenCode: 'AI_STUDIO_AGENTS',
        route: '/ai-studio/agents',
        titleKey: 'aiStudio.home.tiles.agents.title',
        descriptionKey: 'aiStudio.home.tiles.agents.description',
        icon: SparklesIcon,
      },
      {
        id: 'packs',
        screenCode: 'AI_STUDIO_PACKS',
        route: '/ai-studio/packs',
        titleKey: 'aiStudio.home.tiles.packs.title',
        descriptionKey: 'aiStudio.home.tiles.packs.description',
        icon: CubeTransparentIcon,
      },
      {
        id: 'ontology',
        screenCode: 'AI_STUDIO_ONTOLOGY',
        route: '/ai-studio/ontology',
        titleKey: 'aiStudio.home.tiles.ontology.title',
        descriptionKey: 'aiStudio.home.tiles.ontology.description',
        icon: BeakerIcon,
      },
      {
        id: 'conversations',
        screenCode: 'AI_STUDIO_CONVERSATION',
        route: '/ai-studio/conversations',
        titleKey: 'aiStudio.home.tiles.conversations.title',
        descriptionKey: 'aiStudio.home.tiles.conversations.description',
        icon: ChatBubbleLeftRightIcon,
      },
    ],
  },
  {
    id: 'more',
    titleKey: 'aiStudio.home.groups.more',
    tiles: [
      {
        id: 'planning',
        screenCode: 'AI_PLANNING',
        route: '/tenant/planning',
        titleKey: 'aiStudio.home.tiles.planning.title',
        descriptionKey: 'aiStudio.home.tiles.planning.description',
        icon: LightBulbIcon,
      },
      {
        id: 'ai-features',
        screenCode: 'AI_FEATURES',
        route: '/ai/roadmap-generator',
        titleKey: 'aiStudio.home.tiles.aiFeatures.title',
        descriptionKey: 'aiStudio.home.tiles.aiFeatures.description',
        icon: SparklesIcon,
      },
    ],
  },
];
